import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, Database, Lock, Mail, FileText } from 'lucide-react';

const EFFECTIVE_DATE = 'May 1, 2025';
const CONTACT_EMAIL = 'support@mindfulrenewaltx.com';

const PrivacyPolicy = ({ isModal = false, onClose }) => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (isModal && onClose) {
      onClose();
    } else {
      navigate(-1);
    }
  };

  const Section = ({ icon: Icon, title, children }) => (
    <div className="mb-8">
      <div className="flex items-center gap-3 mb-4">
        <div 
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ backgroundColor: 'rgba(201,168,76,0.15)' }}
        >
          <Icon className="w-5 h-5" style={{ color: '#C9A84C' }} />
        </div>
        <h2 
          className="text-lg font-semibold"
          style={{ fontFamily: 'Playfair Display, serif', color: '#1A1A2E' }}
        >
          {title}
        </h2>
      </div>
      <div 
        className="text-sm leading-relaxed space-y-3"
        style={{ fontFamily: 'Jost, sans-serif', color: '#4A4A5A' }}
      >
        {children}
      </div>
    </div>
  );

  return (
    <div 
      className={`min-h-screen ${isModal ? 'fixed inset-0 z-50 overflow-auto' : ''}`}
      style={{ backgroundColor: '#FBF9F6' }}
      data-testid="privacy-policy-page"
    >
      {/* Header */}
      <header className="sticky top-0 z-10 px-4 py-4" style={{ backgroundColor: 'rgba(251,249,246,0.95)', backdropFilter: 'blur(10px)' }}>
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <button
            onClick={handleBack}
            className="p-2 rounded-full transition-colors"
            style={{ backgroundColor: '#F7F1EE' }}
            data-testid="privacy-back-btn"
          >
            <ArrowLeft className="w-5 h-5" style={{ color: '#4A4A5A' }} />
          </button>
          <div>
            <h1 
              className="text-xl font-semibold"
              style={{ fontFamily: 'Playfair Display, serif', color: '#1A1A2E' }}
            >
              Privacy Policy
            </h1>
            <p 
              className="text-sm"
              style={{ fontFamily: 'Jost, sans-serif', color: '#6B6878' }}
            >
              Effective: {EFFECTIVE_DATE}
            </p>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-2xl mx-auto px-4 py-6 pb-32">
        {/* Introduction */}
        <div 
          className="p-5 rounded-2xl mb-8"
          style={{ 
            background: 'linear-gradient(135deg, rgba(201,168,76,0.1) 0%, rgba(232,180,184,0.05) 100%)',
            border: '1px solid rgba(201,168,76,0.2)'
          }}
        >
          <p style={{ fontFamily: 'Jost, sans-serif', color: '#2E2E42' }}>
            Mindful Renewal ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and services.
          </p>
        </div>

        <Section icon={Database} title="Information We Collect">
          <p><strong style={{ color: '#C9A84C' }}>Account Information:</strong> When you create an account, we collect your email address, name (or preferred name), and encrypted password.</p>
          
          <p><strong style={{ color: '#C9A84C' }}>Conversation Data:</strong> Your conversations with Sage (our AI companion) are stored to maintain context and personalize your experience. This includes journal entries, chat messages, and assessment responses.</p>
          
          <p><strong style={{ color: '#C9A84C' }}>Usage Information:</strong> We collect information about how you use the app, including session duration, features accessed, and interaction patterns.</p>
          
          <p><strong style={{ color: '#C9A84C' }}>Device Information:</strong> We may collect device type, operating system, and unique device identifiers for push notifications and analytics.</p>
          
          <p><strong style={{ color: '#C9A84C' }}>Payment Information:</strong> Payment processing is handled by Stripe. We do not store your full credit card number. We receive only the last four digits and expiration date for display purposes.</p>
        </Section>

        <Section icon={Eye} title="How We Use Your Information">
          <p>We use the information we collect to:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Provide and personalize the Sage AI companion experience</li>
            <li>Process your subscription and payments</li>
            <li>Send you relevant notifications (with your permission)</li>
            <li>Improve our services and develop new features</li>
            <li>Communicate with you about your account or support requests</li>
            <li>Comply with legal obligations</li>
          </ul>
        </Section>

        <Section icon={Shield} title="Data Security">
          <p>We implement industry-standard security measures to protect your personal information:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>All data transmission is encrypted using TLS/SSL</li>
            <li>Passwords are hashed using bcrypt (never stored in plain text)</li>
            <li>Access to user data is restricted to authorized personnel only</li>
            <li>Regular security audits and vulnerability assessments</li>
          </ul>
        </Section>

        <Section icon={Lock} title="Data Sharing & Disclosure">
          <p><strong style={{ color: '#C9A84C' }}>We do not sell your personal information.</strong></p>
          
          <p>We may share your information only in these circumstances:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><strong>Service Providers:</strong> With trusted third parties who assist in operating our app (e.g., Stripe for payments, cloud hosting providers)</li>
            <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
            <li><strong>Safety:</strong> If we believe disclosure is necessary to prevent harm to you or others</li>
            <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets (with notice to you)</li>
          </ul>
        </Section>

        <Section icon={FileText} title="Your Rights & Choices">
          <p>You have the right to:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><strong>Access:</strong> Request a copy of your personal data</li>
            <li><strong>Correction:</strong> Update or correct inaccurate information</li>
            <li><strong>Deletion:</strong> Request deletion of your account and associated data</li>
            <li><strong>Portability:</strong> Export your journal entries and chat history</li>
            <li><strong>Opt-out:</strong> Disable push notifications or email communications</li>
          </ul>
          
          <p className="mt-3">To exercise any of these rights, please contact us at <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: '#C9A84C', textDecoration: 'underline' }}>{CONTACT_EMAIL}</a>.</p>
        </Section>

        <Section icon={Database} title="Data Retention">
          <p>We retain your personal information for as long as your account is active or as needed to provide services. If you delete your account, we will delete or anonymize your personal data within 30 days, except where we are required to retain it for legal purposes.</p>
          
          <p>Anonymized, aggregated data (which cannot identify you) may be retained indefinitely for research and service improvement.</p>
        </Section>

        <Section icon={Shield} title="Children's Privacy">
          <p>Mindful Renewal is not intended for users under 18 years of age. We do not knowingly collect personal information from children. If we learn we have collected information from a child under 18, we will delete that information promptly.</p>
        </Section>

        <Section icon={FileText} title="Changes to This Policy">
          <p>We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy in the app and updating the "Effective" date. Your continued use of the app after changes constitutes acceptance of the updated policy.</p>
        </Section>

        <Section icon={Mail} title="Contact Us">
          <p>If you have questions about this Privacy Policy or our data practices, please contact us:</p>
          <div 
            className="mt-3 p-4 rounded-xl"
            style={{ backgroundColor: '#F9F4F0' }}
          >
            <p><strong style={{ color: '#C9A84C' }}>Email:</strong> <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: '#A9756B' }}>{CONTACT_EMAIL}</a></p>
            <p className="mt-2"><strong style={{ color: '#C9A84C' }}>Mindful Renewal</strong></p>
            <p>Houston, Texas</p>
          </div>
        </Section>

        {/* Footer */}
        <div 
          className="mt-8 pt-6 text-center"
          style={{ borderTop: '1px solid #EAE4DC' }}
        >
          <p 
            className="text-xs"
            style={{ fontFamily: 'Jost, sans-serif', color: '#8A8798' }}
          >
            Last updated: {EFFECTIVE_DATE}
          </p>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
